import { ScrollArea } from "@/components/core/scroller";
import { caseRecordSearchQueryOptions } from "@/features/case-record/api/queries/case-records-query";
import { Route } from "@/routes/__root";
import { IconDownload, IconFileTypePdf, IconFileTypeXls, IconGavel, IconSearch, IconTemplate, IconX } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import debounce from "lodash/debounce";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import * as XLSX from "xlsx";
import { Blanket, BlanketImage, BlanketInfo } from "../core/blanket";

type SearchbarProps = {
	value?: string;
	onChange: (value: string) => void;
	onClear?: () => void;
	onEnter?: () => void;
};

export function Searchbar({ value = "", onChange, onClear, onEnter }: SearchbarProps) {
	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter" && onEnter) {
			e.preventDefault();
			onEnter();
		}
	};

	return (
		<div className="flex flex-row items-center w-full gap-1">
			<div className="flex flex-row items-center w-full h-[44px] pl-4 rounded-md overflow-hidden">
				<input
					placeholder="Looking for something?"
					className="flex w-full h-full !border-0 !outline-none !focus:outline-none !focus:border-0 focus:ring-0 p-0 text-base text-foreground"
					value={value}
					autoComplete="off"
					onChange={(e) => onChange(e.target.value)}
					onKeyDown={handleKeyDown}
				/>
				{!!value && onClear && (
					<button
						onClick={() => onClear()}
						type="button"
						className="inline-flex justify-center items-center h-[44px] w-[44px] shrink-0 text-foreground opacity-50 hover:opacity-100"
					>
						<IconX size={18} stroke={1.5} />
					</button>
				)}
			</div>
		</div>
	);
}

interface OmnisearchProps {
	isOpen: boolean;
	onClose: () => void;
}

const Omnisearch = ({ isOpen, onClose }: OmnisearchProps) => {
	const [search, setSearch] = useState(null);
	const [searchValue, setSearchValue] = useState(null);
	const [showDownloadMenu, setShowDownloadMenu] = useState(false);
	const downloadMenuRef = useRef<HTMLDivElement>(null);

	const handleCloseOmnisearch = () => {
		onClose();
	};

	const handleOnChange = () => {
		setSearchValue(search);
	};

	const handleDownloadXLSX = async () => {
		if (!cases || cases.length === 0) {
			alert("No results to download");
			return;
		}

		const worksheetData = [
			["Search Term:", searchValue || "All Cases", ""],
			["Date Generated:", new Date().toLocaleDateString(), ""],
			["Total Results:", cases.length.toString(), ""],
			["", "", ""],
			["Case Number", "Case Title", "Case Category"],
			...cases.map((caseItem) => [caseItem.case_number, caseItem.case_title, caseItem.case_category]),
		];

		const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Case Search Results");

		worksheet["!cols"] = [{ wch: 20 }, { wch: 50 }, { wch: 30 }];

		const timestamp = new Date().toISOString().split("T")[0];
		XLSX.writeFile(workbook, `case_search_results_${timestamp}.xlsx`);
		setShowDownloadMenu(false);
	};

	const wrapText = (text: string, maxWidth: number, font: any, fontSize: number) => {
		const words = text.split(" ");
		const lines: string[] = [];
		let currentLine = "";

		for (const word of words) {
			const testLine = currentLine ? `${currentLine} ${word}` : word;
			const testWidth = font.widthOfTextAtSize(testLine, fontSize);

			if (testWidth <= maxWidth) {
				currentLine = testLine;
			} else {
				if (currentLine) {
					lines.push(currentLine);
					currentLine = word;
				} else {
					// Word is too long, break it
					lines.push(word);
				}
			}
		}

		if (currentLine) {
			lines.push(currentLine);
		}

		return lines;
	};

	const handleDownloadPDF = async () => {
		if (!cases || cases.length === 0) {
			alert("No results to download");
			return;
		}

		const pdfDoc = await PDFDocument.create();
		const page = pdfDoc.addPage([595, 842]);
		const { width, height } = page.getSize();
		const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
		const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

		let yPosition = height - 50;

		page.drawText("Case Search Results", {
			x: 50,
			y: yPosition,
			size: 18,
			font: boldFont,
			color: rgb(0, 0, 0),
		});

		yPosition -= 25;
		page.drawText(`Search Term: ${searchValue || "All Cases"}`, {
			x: 50,
			y: yPosition,
			size: 10,
			font: font,
			color: rgb(0.3, 0.3, 0.3),
		});

		yPosition -= 15;
		page.drawText(`Generated: ${new Date().toLocaleDateString()}`, {
			x: 50,
			y: yPosition,
			size: 10,
			font: font,
			color: rgb(0.5, 0.5, 0.5),
		});

		yPosition -= 15;
		page.drawText(`Total Results: ${cases.length}`, {
			x: 50,
			y: yPosition,
			size: 10,
			font: font,
			color: rgb(0.5, 0.5, 0.5),
		});

		yPosition -= 30;
		page.drawText("Case Number", {
			x: 50,
			y: yPosition,
			size: 10,
			font: boldFont,
			color: rgb(0, 0, 0),
		});
		page.drawText("Case Title", {
			x: 150,
			y: yPosition,
			size: 10,
			font: boldFont,
			color: rgb(0, 0, 0),
		});
		page.drawText("Category", {
			x: 400,
			y: yPosition,
			size: 10,
			font: boldFont,
			color: rgb(0, 0, 0),
		});

		yPosition -= 15;
		let currentPage = page;

		for (const caseItem of cases) {
			// Check if we need a new page (accounting for potential multiline text)
			if (yPosition < 80) {
				currentPage = pdfDoc.addPage([595, 842]);
				yPosition = height - 50;
			}

			const startY = yPosition;

			// Case Number
			currentPage.drawText(caseItem.case_number || "", {
				x: 50,
				y: yPosition,
				size: 9,
				font: font,
				color: rgb(0, 0, 0),
			});

			// Case Title with text wrapping
			const titleText = `${caseItem.case_number || ""} - ${caseItem.case_title || ""}`;
			const titleLines = wrapText(titleText, 220, font, 9); // 220 pixels width for title column
			let currentTitleY = yPosition;

			for (const line of titleLines) {
				currentPage.drawText(line, {
					x: 150,
					y: currentTitleY,
					size: 9,
					font: font,
					color: rgb(0, 0, 0),
				});
				currentTitleY -= 12;
			}

			// Category with text wrapping
			const categoryLines = wrapText(caseItem.case_category || "", 140, font, 9); // 140 pixels width for category column
			let currentCategoryY = yPosition;

			for (const line of categoryLines) {
				currentPage.drawText(line, {
					x: 400,
					y: currentCategoryY,
					size: 9,
					font: font,
					color: rgb(0, 0, 0),
				});
				currentCategoryY -= 12;
			}

			// Calculate the total height used by this row
			const titleHeight = titleLines.length * 12;
			const categoryHeight = categoryLines.length * 12;
			const maxHeight = Math.max(titleHeight, categoryHeight, 12); // At least 12 for case number

			yPosition -= maxHeight + 5; // Add 5 for spacing between rows
		}

		const pdfBytes = await pdfDoc.save();
		const blob = new Blob([pdfBytes], { type: "application/pdf" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		const timestamp = new Date().toISOString().split("T")[0];

		link.href = url;
		link.download = `case_search_results_${timestamp}.pdf`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
		setShowDownloadMenu(false);
	};

	const casesQuery = useSuspenseQuery(
		caseRecordSearchQueryOptions({
			search: searchValue || "Search Here",
		}),
	);
	const meta = casesQuery.data.meta;
	const cases = casesQuery.data?.data;

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}
		return () => {
			document.body.style.overflow = "";
		};
	}, [isOpen]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
				setShowDownloadMenu(false);
			}
		};

		if (showDownloadMenu) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [showDownloadMenu]);

	return ReactDOM.createPortal(
		<AnimatePresence>
			{isOpen && (
				<motion.div
					className={clsx("z-[80] fixed inset-0 flex items-center justify-center min-h-screen w-full overflow-hidden")}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.1 }}
				>
					{/* overlay */}
					<motion.div
						className="fixed inset-0 h-full w-full bg-slate-50/80 pointer-events-none"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.1 }}
					/>
					{/* container */}
					<motion.div
						className="relative flex flex-col items-center h-full w-full overflow-y-auto"
						initial={{ opacity: 0, scale: 0.95 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.95 }}
						transition={{ duration: 0.1 }}
					>
						{/* content */}
						<div className="relative flex flex-col w-full rounded-xl bg-white border border-border/50 shadow-xl shadow-slate-200 duration-200 max-w-[420px] md:max-w-[820px] my-12 md:my-24 overflow-hidden">
							<div className="flex flex-col w-full">
								<div className="flex flex-row w-full border-b border-border/50">
									<Searchbar value={search || ""} onChange={setSearch} onClear={() => setSearch("")} onEnter={handleOnChange} />
									<button
										onClick={handleOnChange}
										type="button"
										className="mt-2 mr-2 inline-flex justify-center items-center w-[32px] h-[32px] rounded-md bg-primary-100 hover:bg-primary-200 text-primary-600"
									>
										<IconSearch size={18} stroke={2} />
									</button>
								</div>
								<ScrollArea className="h-[calc(50svh)]">
									{cases.length !== 0 ? (
										<div className="flex shrink-0 grow flex-col w-full max-h-[540px]">
											{cases.map((result) => {
												return (
													<Link
														key={result.id}
														to={`/case-evaluation/${result.id}`}
														target="_blank"
														className="flex flex-row items-start w-full p-4 gap-2 hover:bg-slate-50 border-b border-border/30 last:border-b-0"
													>
														<div className="relative inline-flex justify-center items-center shrink-0 w-[32px] h-[32px] rounded-full bg-slate-400 text-white mt-0.5">
															<IconGavel size={18} stroke={1.5} />
														</div>
														<div className="flex flex-col w-full min-w-0">
															<div className="text-sm font-bold text-foreground-dark break-all leading-tight">
																{result.case_number} - {result.case_title}
															</div>
															<span className="text-sm text-foreground truncate">{result.case_category}</span>
														</div>
													</Link>
												);
											})}
										</div>
									) : (
										<div className="flex shrink-0 grow flex-col w-full max-h-[540px]">
											<Blanket className="my-auto self-center" width="sm">
												<BlanketImage icon={<IconTemplate size={24} stroke={1.5} />} />
												<BlanketInfo title="No Results" message="Try refining your search query" />
											</Blanket>
										</div>
									)}
								</ScrollArea>

								<div className="flex flex-row items-center justify-between w-full h-[44px] px-4 border-t border-border/50">
									<div className="relative" ref={downloadMenuRef}>
										<button
											onClick={() => setShowDownloadMenu(!showDownloadMenu)}
											type="button"
											className="inline-flex items-center gap-1 text-base text-foreground/50 hover:text-foreground-dark"
											disabled={!cases || cases.length === 0}
										>
											<IconDownload size={16} stroke={1.5} />
											Download
										</button>
										{showDownloadMenu && cases && cases.length > 0 && (
											<div className="absolute bottom-full left-0 mb-2 w-48 rounded-md bg-white border border-border shadow-lg">
												<button
													onClick={handleDownloadXLSX}
													type="button"
													className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm hover:bg-slate-50"
												>
													<IconFileTypeXls size={16} stroke={1.5} className="text-green-600" />
													Download as Excel
												</button>
												<button
													onClick={handleDownloadPDF}
													type="button"
													className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm hover:bg-slate-50"
												>
													<IconFileTypePdf size={16} stroke={1.5} className="text-red-600" />
													Download as PDF
												</button>
											</div>
										)}
									</div>
									<button onClick={handleCloseOmnisearch} type="button" className="text-base text-foreground/50 hover:text-foreground-dark">
										Close
									</button>
								</div>
							</div>
						</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>,
		document.body,
	);
};

export default Omnisearch;
